/**
 * 會計核心模組的 API hooks(REQ-20260806002)。
 * 四個模組(出貨 / 進貨 / 應收 / 應付)共用同一份,避免各自定義出不同的 queryKey。
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  PURCHASE_ATTACHMENT_ALLOWED_MIMES,
  PURCHASE_ATTACHMENT_MAX_BYTES,
  shipmentReceiptTotal,
  type ApiSuccess,
  type ApSettlementDetail,
  type ArSettlementDetail,
  type CompanyProfile,
  type CreateApSettlementInput,
  type CreateArSettlementInput,
  type CreatePurchaseOrderInput,
  type CreateSalesOrderCreditNoteInput,
  type CreateSalesOrderInput,
  type CreateShipmentInput,
  type CustomerStatement,
  type InterviewImportResponse,
  type OrderShipmentsResponse,
  type PartyLookupResponse,
  type PostPurchaseOrderResponse,
  type PresignPurchaseUploadResponse,
  type PurchaseOrderAttachmentResponse,
  type PurchaseOrderDetail,
  type PurchaseOrderListItem,
  type PurchaseOrderListResponse,
  type SalesOrderCreditNoteItem,
  type SalesOrderCreditNoteListResponse,
  type SalesOrderDetail,
  type SalesOrderListItem,
  type SalesOrderListResponse,
  type SalesPersonListResponse,
  type StatementQuery,
  type SupplierStatement,
  type UnshippedSummaryResponse,
  type UpdatePurchaseOrderInput,
  type UpdateSalesOrderInput,
} from '@app/shared';
import axios from 'axios';
import { apiClient } from '../../lib/api-client';

// ─── 主檔連動 ─────────────────────────────────────────

/** 輸入編號 / 名稱 → 帶出統編、地址、電話。keyword 太短時不打 API。 */
export function usePartyLookup(
  type: 'customer' | 'supplier',
  keyword: string,
): ReturnType<typeof useQuery<PartyLookupResponse>> {
  const trimmed = keyword.trim();
  return useQuery<PartyLookupResponse>({
    queryKey: ['accounting', 'parties', type, trimmed],
    enabled: trimmed.length > 0,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PartyLookupResponse>>('/accounting/parties', {
        params: { type, keyword: trimmed },
      });
      return res.data.data;
    },
  });
}

/**
 * 用 id 取單一主檔(REQ-20260815001)。
 *
 * 深連結進來時客戶是誰已經確定了(訪談頁帶過來的 customerId),不需要也不應該
 * 拿關鍵字再猜一次。queryKey 與 `usePartyLookup()` 分開,避免關鍵字查詢的快取
 * 被單筆結果覆蓋。找不到時後端回空 items,呼叫端自己決定怎麼呈現。
 */
export function usePartyById(
  type: 'customer' | 'supplier',
  id: number | undefined,
): ReturnType<typeof useQuery<PartyLookupResponse>> {
  return useQuery<PartyLookupResponse>({
    queryKey: ['accounting', 'parties', type, 'id', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PartyLookupResponse>>('/accounting/parties', {
        params: { type, id },
      });
      return res.data.data;
    },
  });
}

/**
 * 業務員下拉的選項(REQ-20260815002)。
 *
 * **不是走 `useUsers()`**:`GET /users` 掛 `@Roles('ADMIN')`,業務自己開單時會 403、
 * 下拉整個空掉。這支只回 id + displayName,放行角色與會計頁其他查詢一致。
 * 清單幾乎不變動,快取 5 分鐘避免每開一次表單就打一次。
 */
export function useSalesPersons(): ReturnType<typeof useQuery<SalesPersonListResponse>> {
  return useQuery<SalesPersonListResponse>({
    queryKey: ['accounting', 'sales-persons'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesPersonListResponse>>(
        '/accounting/sales-persons',
      );
      return res.data.data;
    },
  });
}

/**
 * 請款文件抬頭的「我方公司」資料(REQ-20260817003)。
 *
 * **不是走 `useEinvoiceSetting()`**:`GET /settings/einvoice` 掛 `@Roles('ADMIN')`,
 * 會計頁的 SALES / VIEWER 會 403、抬頭整個空掉(同 `useSalesPersons()` 的理由)。
 * 這支只回名稱 / 地址 / 電話 / 傳真;沒設定過時是四個空字串,呼叫端留白即可。
 * 一份設定幾乎不變動,快取 5 分鐘。
 */
export function useCompanyProfile(): ReturnType<typeof useQuery<CompanyProfile>> {
  return useQuery<CompanyProfile>({
    queryKey: ['accounting', 'company-profile'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<CompanyProfile>>('/accounting/company-profile');
      return res.data.data;
    },
  });
}

/** 某客戶可導入的訪談紀錄(規格候選文字 + 商品圖片) */
export function useInterviewImport(
  customerId: number | undefined,
  enabled: boolean,
): ReturnType<typeof useQuery<InterviewImportResponse>> {
  return useQuery<InterviewImportResponse>({
    queryKey: ['accounting', 'interview-import', customerId],
    enabled: enabled && customerId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<InterviewImportResponse>>(
        `/accounting/customers/${customerId}/interviews`,
      );
      return res.data.data;
    },
  });
}

// ─── 出貨管理 ─────────────────────────────────────────

export interface SalesOrderFilter {
  customerId?: number | undefined;
  /** 訪談頁建應收單前查「這筆訪談是不是已經開過單」用 */
  interviewId?: number | undefined;
  status?: string | undefined;
  period?: string | undefined;
  /** 自訂區間 YYYY-MM-DD,`dateTo` 含當天;與 `period` 擇一送(REQ-20260929002) */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  keyword?: string | undefined;
  /** 不帶時後端預設 limit=50 / offset=0 */
  limit?: number | undefined;
  offset?: number | undefined;
  /**
   * 條件還沒齊全時先不要查(REQ-20260819002)。不傳 = `true` = 原本的行為。
   *
   * 需要它的原因很具體:進貨單要用「代管客戶的近期單號」當建議清單,但客戶還沒選時
   * `customerId` 是 `undefined`,後端的 `if (filter.customerId != null)` 會整段跳過 ——
   * 送出去的等於「查全部訂單」,一次跟這張表單無關的全量查詢。
   */
  enabled?: boolean | undefined;
}

export function useSalesOrders(
  filter: SalesOrderFilter,
): ReturnType<typeof useQuery<SalesOrderListResponse>> {
  // `enabled` 是查不查的開關,不是查詢條件 —— 要從 queryKey 與送出的 params 裡拿掉,
  // 否則會變成 `?enabled=true` 這種後端看不懂的參數
  const { enabled, ...params } = filter;
  return useQuery<SalesOrderListResponse>({
    queryKey: ['sales-orders', params],
    enabled: enabled ?? true,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesOrderListResponse>>('/sales-orders', {
        params: cleanParams(params),
      });
      return res.data.data;
    },
  });
}

export function useSalesOrder(
  id: number | undefined,
): ReturnType<typeof useQuery<SalesOrderDetail>> {
  return useQuery<SalesOrderDetail>({
    queryKey: ['sales-orders', 'detail', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesOrderDetail>>(`/sales-orders/${id}`);
      return res.data.data;
    },
  });
}

/**
 * 建單畫面的單號預覽(REQ-20260813004)。回的是「若現在建單會拿到的號碼」。
 *
 * ⚠️ **預覽值,不是保證值**:序號由後端在寫入當下決定、撞號還會重算重試
 * (CLAUDE.md §6.3),中間有人建了單就會不一樣。這支不佔號、不上鎖,所以畫面上
 * 一定要標示成「預覽」——「前端自己算 seq」這條路已經被 REQ-20260809002 §五 否掉,
 * 這裡是改由後端回同一份邏輯算出來的號,不是在前端重寫一份。
 *
 * `staleTime: 0`:單號隨單據日期變,快取住舊日期段的號只會顯示過期資訊。
 */
export function useNextSalesOrderNo(
  date: string | undefined,
): ReturnType<typeof useQuery<string>> {
  return useQuery<string>({
    queryKey: ['sales-orders', 'next-order-no', date],
    enabled: date != null && date !== '',
    staleTime: 0,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ orderNo: string }>>(
        '/sales-orders/next-order-no',
        { params: { date } },
      );
      return res.data.data.orderNo;
    },
  });
}

export function useCreateSalesOrder(): UseMutationResult<
  SalesOrderDetail,
  Error,
  CreateSalesOrderInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSalesOrderInput) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderDetail>>('/sales-orders', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/** 改草稿。後端只放行 DRAFT,擋下來的訊息直接透出給使用者。 */
export function useUpdateSalesOrder(): UseMutationResult<
  SalesOrderDetail,
  Error,
  { id: number } & UpdateSalesOrderInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: number } & UpdateSalesOrderInput) => {
      const res = await apiClient.patch<ApiSuccess<SalesOrderDetail>>(
        `/sales-orders/${id}`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/** 刪草稿(204,沒有回傳內容)。 */
export function useDeleteSalesOrder(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/sales-orders/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

export function usePostSalesOrder(): UseMutationResult<SalesOrderDetail, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderDetail>>(`/sales-orders/${id}/post`);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/** 作廢已過帳的單。已有沖帳紀錄時後端會擋,錯誤訊息要原樣顯示給使用者。 */
export function useVoidSalesOrder(): UseMutationResult<
  SalesOrderDetail,
  Error,
  { id: number; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderDetail>>(
        `/sales-orders/${id}/void`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

// ─── 出貨紀錄(REQ-20260815008)────────────────────────

/**
 * 這張單的出貨進度 + 歷次出貨紀錄。
 *
 * 已出貨數量是後端即時聚合的(不是欄位),所以這支要能被 invalidate ——
 * 登記完出貨後未出貨數量會變,不重抓的話彈窗會停在舊數字。
 */
export function useOrderShipments(
  orderId: number | undefined,
): ReturnType<typeof useQuery<OrderShipmentsResponse>> {
  return useQuery<OrderShipmentsResponse>({
    queryKey: ['shipments', 'order', orderId],
    enabled: orderId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<OrderShipmentsResponse>>(
        `/sales-orders/${orderId}/shipments`,
      );
      return res.data.data;
    },
  });
}

/**
 * 登記一次出貨(可分批)。超賣由後端擋,錯誤訊息要原樣顯示給使用者 ——
 * 前端的檢查只是為了不用等一次來回,不是權威(同 ReceiptModal 的做法)。
 *
 * 出貨會改變三個地方的數字:這張單的進度、出貨管理列表的出貨欄、
 * 儀表板的未出貨彙總,所以三組 cache 都要失效。
 *
 * 儀表板自己那組彙總(`['dashboard']`)也一起失效(REQ-20260826002):深連結進來的
 * 那條路徑送出成功後會**被導回儀表板**,不順手重查的話會吃到全域 30 秒 staleTime
 * 的舊快取 —— 使用者看到的就是「貨出了、但首頁數字沒動」。
 *
 * 順便收款時(REQ-20260815010)還多改了應收那一側的數字,再失效收款相關的三組 ——
 * 只出貨沒收款時不動它們,不必要的重查也是成本。
 */
export function useCreateShipment(
  orderId: number | undefined,
): UseMutationResult<OrderShipmentsResponse, Error, CreateShipmentInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateShipmentInput) => {
      const res = await apiClient.post<ApiSuccess<OrderShipmentsResponse>>(
        `/sales-orders/${orderId}/shipments`,
        input,
      );
      return res.data.data;
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['shipments'] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (shipmentReceiptTotal(input) > 0) {
        void qc.invalidateQueries({ queryKey: ['ar-outstanding'] });
        void qc.invalidateQueries({ queryKey: ['statements'] });
        void qc.invalidateQueries({ queryKey: ['ar-settlements'] });
      }
      /*
       * 委外貨運(REQ-20260908002)。這次只多了一筆**運費明細**,
       * **進貨單 / 應付 / 報表一個都沒有動** —— 那要等到月底歸單才會發生。
       * 所以只失效運費歸單畫面那一組(它列的就是未歸單的明細)。
       *
       * ⚠️ 推翻 REQ-20260829003 在這裡失效 `purchase-orders` / `ap-outstanding` /
       * `ap-settlements` / `ar-ap-reports` 的做法:出貨端已經不入帳了,
       * 留著只是白重查四組 cache。
       */
      if (input.shippingMethod === 'CARRIER') {
        void qc.invalidateQueries({ queryKey: ['freight-lines'] });
      }
    },
  });
}

/**
 * 改一筆既有出貨紀錄的**出貨日期**(REQ-20260825003)。
 *
 * 只送日期一格。已有沖帳金額的單後端回 400,訊息要原樣顯示給使用者 ——
 * 前端不預先判斷能不能改(慣例同 REQ-20260807001)。
 *
 * 失效範圍沿用 `useCreateShipment`:日期會影響這張單的歷次紀錄,也會影響
 * 訪談徽章上的「最近出貨日」(REQ-20260824008),兩組 cache 都在這兩把鑰匙底下。
 * 金額一毛都沒動,所以不碰收款那三組。
 */
export function useUpdateShipmentDate(
  orderId: number | undefined,
): UseMutationResult<OrderShipmentsResponse, Error, { shipmentId: number; shippedAt: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shipmentId, shippedAt }) => {
      const res = await apiClient.patch<ApiSuccess<OrderShipmentsResponse>>(
        `/sales-orders/${orderId}/shipments/${shipmentId}`,
        { shippedAt },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shipments'] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/** 未出貨彙總(儀表板)。只算已過帳的單,見後端 `unshippedSummary()`。 */
export function useUnshippedSummary(): ReturnType<typeof useQuery<UnshippedSummaryResponse>> {
  return useQuery<UnshippedSummaryResponse>({
    queryKey: ['shipments', 'unshipped-summary'],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<UnshippedSummaryResponse>>(
        '/shipments/unshipped-summary',
      );
      return res.data.data;
    },
  });
}

// ─── 進貨管理 ─────────────────────────────────────────

export interface PurchaseOrderFilter {
  supplierId?: number | undefined;
  customerId?: number | undefined;
  orderType?: string | undefined;
  status?: string | undefined;
  /** 到貨狀態(REQ-20260923001):`PENDING` = 還沒填到貨日期、`RECEIVED` = 已填 */
  received?: string | undefined;
  period?: string | undefined;
  keyword?: string | undefined;
}

export function usePurchaseOrders(
  filter: PurchaseOrderFilter,
): ReturnType<typeof useQuery<PurchaseOrderListResponse>> {
  return useQuery<PurchaseOrderListResponse>({
    queryKey: ['purchase-orders', filter],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PurchaseOrderListResponse>>('/purchase-orders', {
        params: cleanParams(filter),
      });
      return res.data.data;
    },
  });
}

export function usePurchaseOrder(
  id: number | undefined,
): ReturnType<typeof useQuery<PurchaseOrderDetail>> {
  return useQuery<PurchaseOrderDetail>({
    queryKey: ['purchase-orders', 'detail', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PurchaseOrderDetail>>(`/purchase-orders/${id}`);
      return res.data.data;
    },
  });
}

/** 進貨單的單號預覽(REQ-20260813004)。取捨與 `useNextSalesOrderNo()` 完全相同 */
export function useNextPurchaseOrderNo(
  date: string | undefined,
): ReturnType<typeof useQuery<string>> {
  return useQuery<string>({
    queryKey: ['purchase-orders', 'next-order-no', date],
    enabled: date != null && date !== '',
    staleTime: 0,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ orderNo: string }>>(
        '/purchase-orders/next-order-no',
        { params: { date } },
      );
      return res.data.data.orderNo;
    },
  });
}

export function useCreatePurchaseOrder(): UseMutationResult<
  PurchaseOrderDetail,
  Error,
  CreatePurchaseOrderInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePurchaseOrderInput) => {
      const res = await apiClient.post<ApiSuccess<PurchaseOrderDetail>>('/purchase-orders', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/**
 * 改草稿(REQ-20260814001)。後端只放行 DRAFT —— 送出確認後要先退回草稿,
 * 已確認的單只能折讓或作廢。擋下來的訊息直接透出給使用者。
 */
export function useUpdatePurchaseOrder(): UseMutationResult<
  PurchaseOrderDetail,
  Error,
  { id: number } & UpdatePurchaseOrderInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: number } & UpdatePurchaseOrderInput) => {
      const res = await apiClient.patch<ApiSuccess<PurchaseOrderDetail>>(
        `/purchase-orders/${id}`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/**
 * 作廢已確認的進貨單(REQ-20260814001)。**後端只允許 POSTED**,
 * 已有沖帳紀錄時也會擋,錯誤訊息要原樣顯示給使用者。
 */
export function useVoidPurchaseOrder(): UseMutationResult<
  PurchaseOrderDetail,
  Error,
  { id: number; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await apiClient.post<ApiSuccess<PurchaseOrderDetail>>(
        `/purchase-orders/${id}/void`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/** 送出確認:DRAFT → 待確認。送出後單據凍結,只能退回草稿或確認(REQ-20260813001) */
export function useSubmitPurchaseOrder(): UseMutationResult<PurchaseOrderDetail, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post<ApiSuccess<PurchaseOrderDetail>>(
        `/purchase-orders/${id}/submit`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/** 退回草稿:待確認 → DRAFT。退回原因會寫進備註 */
export function useReturnPurchaseOrderToDraft(): UseMutationResult<
  PurchaseOrderDetail,
  Error,
  { id: number; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const res = await apiClient.post<ApiSuccess<PurchaseOrderDetail>>(
        `/purchase-orders/${id}/return-to-draft`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/**
 * 確認(= 過帳):回傳有沒有拋轉代管庫存,前端要把結果講給使用者聽。
 * 端點在 REQ-20260813001 由 `/post` 改名為 `/confirm`,語意與畫面按鈕一致。
 */
export function useConfirmPurchaseOrder(): UseMutationResult<
  PostPurchaseOrderResponse,
  Error,
  number
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post<ApiSuccess<PostPurchaseOrderResponse>>(
        `/purchase-orders/${id}/confirm`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      void qc.invalidateQueries({ queryKey: ['inventory-records'] });
    },
  });
}

export function useDeletePurchaseOrder(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/purchase-orders/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/**
 * 進貨單附件上傳(REQ-20260813001)。三步驟同存貨紀錄圖片:
 * presign → 直傳 MinIO(Content-Type 必須與 presign 一致)→ 回寫 DB row。
 */
export async function uploadPurchaseOrderAttachment(
  orderId: number,
  file: File,
): Promise<PurchaseOrderAttachmentResponse> {
  if (!(PURCHASE_ATTACHMENT_ALLOWED_MIMES as readonly string[]).includes(file.type)) {
    throw new Error(`不支援的檔案類型:${file.type || '未知'}(限 JPG / PNG / PDF)`);
  }
  if (file.size > PURCHASE_ATTACHMENT_MAX_BYTES) {
    throw new Error(`檔案過大,上限 ${PURCHASE_ATTACHMENT_MAX_BYTES / 1024 / 1024} MB`);
  }
  const mimeType = file.type as (typeof PURCHASE_ATTACHMENT_ALLOWED_MIMES)[number];

  const presign = await apiClient.post<ApiSuccess<PresignPurchaseUploadResponse>>(
    `/purchase-orders/${orderId}/attachments/presign`,
    { originalName: file.name, mimeType, sizeBytes: file.size },
  );
  const { uploadUrl, storedName, storagePath } = presign.data.data;

  await axios.put(uploadUrl, file, { headers: { 'Content-Type': mimeType } });

  const created = await apiClient.post<ApiSuccess<PurchaseOrderAttachmentResponse>>(
    `/purchase-orders/${orderId}/attachments`,
    { originalName: file.name, mimeType, sizeBytes: file.size, storedName, storagePath },
  );
  return created.data.data;
}

export async function deletePurchaseOrderAttachment(
  orderId: number,
  attachmentId: number,
): Promise<void> {
  await apiClient.delete(`/purchase-orders/${orderId}/attachments/${attachmentId}`);
}

/**
 * 附件的預覽 / 下載網址(REQ-20260901010)。寫法與存貨紀錄圖片的
 * `getInventoryImageUrl` 一致 —— 後端 presign 時就帶了 `inline` 的
 * Content-Disposition,所以這個網址可以直接當 `<img src>` 或新分頁開啟。
 */
export async function getPurchaseOrderAttachmentUrl(
  orderId: number,
  attachmentId: number,
): Promise<string> {
  const res = await apiClient.get<ApiSuccess<{ url: string; originalName: string }>>(
    `/purchase-orders/${orderId}/attachments/${attachmentId}/signed-url`,
  );
  return res.data.data.url;
}

export function usePurchaseOrderAttachmentUrl(orderId: number, attachmentId: number) {
  return useQuery({
    queryKey: ['purchase-order-attachment-url', orderId, attachmentId],
    // signed URL 5 分鐘過期,設 stale 4 分鐘
    staleTime: 1000 * 60 * 4,
    queryFn: () => getPurchaseOrderAttachmentUrl(orderId, attachmentId),
  });
}

// ─── 沖帳:未結明細 ───────────────────────────────────

export function useArOutstanding(
  customerId: number | undefined,
): ReturnType<typeof useQuery<SalesOrderListItem[]>> {
  return useQuery<SalesOrderListItem[]>({
    queryKey: ['ar-outstanding', customerId],
    enabled: customerId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesOrderListItem[]>>(
        `/customers/${customerId}/ar-outstanding`,
      );
      return res.data.data;
    },
  });
}

export function useApOutstanding(
  supplierId: number | undefined,
): ReturnType<typeof useQuery<PurchaseOrderListItem[]>> {
  return useQuery<PurchaseOrderListItem[]>({
    queryKey: ['ap-outstanding', supplierId],
    enabled: supplierId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PurchaseOrderListItem[]>>(
        `/suppliers/${supplierId}/ap-outstanding`,
      );
      return res.data.data;
    },
  });
}

/**
 * 手動登錄收款(REQ-20260807003)。
 *
 * 收款會同時改變三個畫面的數字,所以成功後這幾組 cache 都要失效:
 * 未結明細(這張單還欠多少)、月結對帳單(本期收款 / 期末餘額)、
 * 沖帳紀錄列表,以及出貨單列表上的「已收 / 未結」欄位。
 */
export function useCreateArSettlement(
  customerId: number | undefined,
): UseMutationResult<ArSettlementDetail, Error, CreateArSettlementInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateArSettlementInput) => {
      const res = await apiClient.post<ApiSuccess<ArSettlementDetail>>(
        `/customers/${customerId}/ar-settlements`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ar-outstanding', customerId] });
      void qc.invalidateQueries({ queryKey: ['statements'] });
      void qc.invalidateQueries({ queryKey: ['ar-settlements'] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/**
 * 解除沖銷(REQ-20260821001)。**ADMIN only**,後端會擋。
 *
 * 成功後受影響的數字與登錄收款完全一樣(只是方向相反),所以失效同一組 cache:
 * 未結明細會多出那張單、對帳單的本期收款變少、沖帳紀錄多一列負金額、
 * 出貨單列表的「已收 / 未結」跟著回到未收款(順向鎖定也因此自動解開)。
 */
export function useReverseArSettlement(
  customerId: number | undefined,
): UseMutationResult<ArSettlementDetail, Error, { settlementId: number; reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ settlementId, reason }: { settlementId: number; reason: string }) => {
      const res = await apiClient.post<ApiSuccess<ArSettlementDetail>>(
        `/ar-settlements/${settlementId}/reverse`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ar-outstanding', customerId] });
      void qc.invalidateQueries({ queryKey: ['statements'] });
      void qc.invalidateQueries({ queryKey: ['ar-settlements'] });
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

/**
 * 作廢並複製新單的「複製」那一半(REQ-20260821001)。
 * 回傳的是新建的草稿,呼叫端負責把使用者帶過去編輯。
 */
export function useDuplicateSalesOrder(): UseMutationResult<SalesOrderDetail, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderDetail>>(
        `/sales-orders/${id}/duplicate`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sales-orders'] });
    },
  });
}

// ─── 銷貨折讓單(REQ-20260821001)──────────────────────

/** 某張出貨單開過的折讓單 —— 開新折讓單時要看得到已經開過多少 */
export function useOrderCreditNotes(
  orderId: number | undefined,
): ReturnType<typeof useQuery<SalesOrderCreditNoteListResponse>> {
  return useQuery<SalesOrderCreditNoteListResponse>({
    queryKey: ['credit-notes', 'order', orderId],
    enabled: orderId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesOrderCreditNoteListResponse>>(
        `/sales-orders/${orderId}/credit-notes`,
      );
      return res.data.data;
    },
  });
}

/**
 * 開一張折讓單。**不影響出貨單的未結餘額** —— 折讓單只進月結對帳單那一期的
 * 「本期折讓」(見後端 credit-notes.service.ts 檔頭),所以只失效對帳單與折讓單本身。
 */
export function useCreateCreditNote(
  orderId: number | undefined,
): UseMutationResult<SalesOrderCreditNoteItem, Error, CreateSalesOrderCreditNoteInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSalesOrderCreditNoteInput) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderCreditNoteItem>>(
        `/sales-orders/${orderId}/credit-notes`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credit-notes'] });
      void qc.invalidateQueries({ queryKey: ['statements'] });
    },
  });
}

/**
 * 審核一張折讓單(REQ-20260821002)——「審核後」那筆金額才會被月結對帳單扣除。
 *
 * 失效的兩組與 `useCreateCreditNote()` 相同(折讓單清單本身 + 對帳單),
 * **不含出貨單** —— 折讓單從頭到尾不影響出貨單的未結餘額(REQ-20260821001 §2.4)。
 * 沒有帶 `orderId` 參數:URL 用的是折讓單自己的 id,而 `['credit-notes']` 這個
 * 前綴已經涵蓋 `['credit-notes', 'order', orderId]`。
 */
export function useReviewCreditNote(): UseMutationResult<SalesOrderCreditNoteItem, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (creditNoteId: number) => {
      const res = await apiClient.post<ApiSuccess<SalesOrderCreditNoteItem>>(
        `/credit-notes/${creditNoteId}/review`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credit-notes'] });
      void qc.invalidateQueries({ queryKey: ['statements'] });
    },
  });
}

/**
 * 手動登錄付款(REQ-20260815002)—— 對稱於 `useCreateArSettlement()`。
 *
 * 付款會同時改變三個地方的數字,成功後這幾組 cache 都要失效:
 * 未結應付明細(這張單還欠多少)、沖帳紀錄列表,以及進貨單列表上的
 * 「已付 / 未結」欄位。另外報表分頁的應付數字也是同一批單據算出來的,一併失效。
 */
export function useCreateApSettlement(
  supplierId: number | undefined,
): UseMutationResult<ApSettlementDetail, Error, CreateApSettlementInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateApSettlementInput) => {
      const res = await apiClient.post<ApiSuccess<ApSettlementDetail>>(
        `/suppliers/${supplierId}/ap-settlements`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ap-outstanding', supplierId] });
      void qc.invalidateQueries({ queryKey: ['ap-settlements'] });
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      void qc.invalidateQueries({ queryKey: ['ar-ap-reports'] });
    },
  });
}

/**
 * 應付側的解除沖銷(REQ-20261029002)—— 對稱於 `useReverseArSettlement()`,ADMIN only。
 *
 * 失效的 cache 比登錄付款多一組 `statements`:反向單的沖帳日期是今天,
 * 同一頁下方的廠商對帳單「本期付款」會跟著變(登錄付款那支沒失效它是既有行為,本次不動)。
 */
export function useReverseApSettlement(
  supplierId: number | undefined,
): UseMutationResult<ApSettlementDetail, Error, { settlementId: number; reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ settlementId, reason }: { settlementId: number; reason: string }) => {
      const res = await apiClient.post<ApiSuccess<ApSettlementDetail>>(
        `/ap-settlements/${settlementId}/reverse`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ap-outstanding', supplierId] });
      void qc.invalidateQueries({ queryKey: ['ap-settlements'] });
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      void qc.invalidateQueries({ queryKey: ['ar-ap-reports'] });
      void qc.invalidateQueries({ queryKey: ['statements'] });
    },
  });
}

// ─── 月結對帳單 ───────────────────────────────────────

/**
 * 月結對帳單。期間可以是自然月(`period`)或任意起訖日期(`from` + `to`,
 * REQ-20260815013)—— 後端是同一支端點、同一套滾動式,只有期間邊界不同。
 */
export function useCustomerStatement(
  customerId: number | undefined,
  query: StatementQuery,
): ReturnType<typeof useQuery<CustomerStatement>> {
  const ready =
    query.from != null && query.to != null ? true : /^\d{4}-\d{2}$/.test(query.period ?? '');
  return useQuery<CustomerStatement>({
    queryKey: ['statements', 'customer', customerId, query],
    enabled: customerId != null && ready,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<CustomerStatement>>(
        `/statements/customer/${customerId}`,
        { params: query },
      );
      return res.data.data;
    },
  });
}

/**
 * 廠商對帳單(REQ-20260831001)。期間的兩種指定方式與 `enabled` 條件
 * 完全比照上面那支 —— 後端也是同一個 query schema。
 */
export function useSupplierStatement(
  supplierId: number | undefined,
  query: StatementQuery,
): ReturnType<typeof useQuery<SupplierStatement>> {
  const ready =
    query.from != null && query.to != null ? true : /^\d{4}-\d{2}$/.test(query.period ?? '');
  return useQuery<SupplierStatement>({
    queryKey: ['statements', 'supplier', supplierId, query],
    enabled: supplierId != null && ready,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SupplierStatement>>(
        `/statements/supplier/${supplierId}`,
        { params: query },
      );
      return res.data.data;
    },
  });
}

/**
 * 某個人的私帳未結明細(REQ-20260810002)。
 * 回傳形狀與 `useArOutstanding()` 相同,所以表格欄位可以整套沿用。
 */
export function usePersonalOutstanding(
  customerId: number | undefined,
  payerName: string | null,
): ReturnType<typeof useQuery<SalesOrderListItem[]>> {
  return useQuery<SalesOrderListItem[]>({
    queryKey: ['statements', 'personal-outstanding', customerId, payerName],
    enabled: customerId != null && !!payerName,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<SalesOrderListItem[]>>(
        `/statements/customer/${customerId}/personal-outstanding`,
        { params: { payerName } },
      );
      return res.data.data;
    },
  });
}

/** 這個客戶用過的私帳對象姓名(下拉 / 自動完成用,不是 reference 表) */
export function usePayerNames(
  customerId: number | undefined,
): ReturnType<typeof useQuery<string[]>> {
  return useQuery<string[]>({
    queryKey: ['statements', 'payer-names', customerId],
    enabled: customerId != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<string[]>>(
        `/statements/customer/${customerId}/payer-names`,
      );
      return res.data.data;
    },
  });
}

/** 把 undefined / 空字串的欄位拿掉,避免 axios 送出 `?status=` 這種空參數 */
function cleanParams<T extends object>(filter: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filter).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  );
}
